import type {
    ClientRequest,
    ClientResponse,
    TaxCategory,
    TaxCategoryDraft,
    TaxCategoryUpdateAction,
} from '@commercetools/platform-sdk';

import type { ApiClientDetails, QueryArgs } from '../../types';
import { getQueryPredicate, QueryKey } from '../utils';

export function taxApi({ apiRoot, ctpClient }: ApiClientDetails) {
    function taxCategoryClientRequest(args?: QueryArgs): ClientRequest {
        return apiRoot().taxCategories().get(args).clientRequest();
    }

    function fetchTaxCategories(args?: QueryArgs): Promise<TaxCategory[]> {
        const clientRequest = taxCategoryClientRequest(args);

        return ctpClient().process(clientRequest, payload => Promise.resolve(payload.body.results), {
            accumulate: true,
        });
    }

    function fetchAllTaxCategories(expand?: string | string[]): Promise<TaxCategory[]> {
        return fetchTaxCategories({ queryArgs: { expand } });
    }

    async function getTaxCategoryById(id: string): Promise<TaxCategory> {
        return (await apiRoot().taxCategories().withId({ ID: id }).get().execute()).body;
    }

    function getTaxCategoriesByIds(ids: string[]): Promise<TaxCategory[]> {
        return fetchTaxCategories({
            queryArgs: { where: getQueryPredicate(ids, QueryKey.ID) },
        });
    }

    function createTaxCategory(taxCategory: TaxCategoryDraft): Promise<ClientResponse<TaxCategory>> {
        return apiRoot()
            .taxCategories()
            .post({
                body: taxCategory,
            })
            .execute();
    }

    async function getTaxCategory(key: string): Promise<TaxCategory> {
        return (
            await apiRoot()
                .taxCategories()
                .withKey({
                    key,
                })
                .get()
                .execute()
        ).body;
    }

    function updateTaxRatesForCategory(
        key: string,
        version: number,
        actions: TaxCategoryUpdateAction[]
    ): Promise<ClientResponse<TaxCategory>> {
        return apiRoot()
            .taxCategories()
            .withKey({ key })
            .post({
                body: {
                    version,
                    actions,
                },
            })
            .execute();
    }

    function createTaxCategories(taxCategories: TaxCategoryDraft[]): Promise<ClientResponse<TaxCategory>>[] {
        return taxCategories.map(createTaxCategory);
    }

    return {
        fetchAllTaxCategories,
        getTaxCategoryById,
        getTaxCategoriesByIds,
        createTaxCategories,
        createTaxCategory,
        getTaxCategory,
        updateTaxRatesForCategory,
    };
}
